import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../components/config.js';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [encryptionKey, setEncryptionKey] = useState(null);
  const [comsappLinking, setComsappLinking] = useState(null);
  const [relinkingLoading, setRelinkingLoading] = useState(false);

  // Helper: Extract and expose external account linking info for UI
  const updateLinkingStatus = (user) => {
    if (user?.externalAccounts?.comsapp) {
      setComsappLinking(user.externalAccounts.comsapp);
    } else {
      setComsappLinking(null);
    }
  };

  // Check if user is already logged in on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');
      const encryptionKeyFromStorage = localStorage.getItem('encryptionKey');
      
      if (token) {
        try {
          // Set the token in the headers for the API call
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          const response = await axios.get(`${API_URL}/api/users/me`);
          
          // Store the entire user object with permission flags
          const user = {
            ...response.data,
            education: response.data.education || false,
            health: response.data.health || false,
            harvesting: response.data.harvesting || false,
            communityOutreach: response.data.communityOutreach || false,
          };
          
          setCurrentUser(user);
          updateLinkingStatus(user);
          setEncryptionKey(encryptionKeyFromStorage);
        } catch (error) {
          console.error('Error checking auth status', error);
          localStorage.removeItem('authToken');
          setCurrentUser(null);
          setComsappLinking(null);
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (!currentUser || !currentUser.cardIssueFlag) return;

    const warningVersion = `${currentUser.id || currentUser.email || 'user'}:${currentUser.cardIssueUpdatedAt || 'no-date'}`;
    const shownKey = `cardIssueWarningShown:${warningVersion}`;

    if (sessionStorage.getItem(shownKey)) return;

    const message = currentUser.cardIssueMessage ||
      'Your citizen card appears inactive or expired. Please contact Registry at 807-623-4635.';

    window.alert(message);
    sessionStorage.setItem(shownKey, '1');
  }, [currentUser]);

  useEffect(() => {
    const autoProvision = async () => {
      if (!currentUser?.email) return;

      const userId = currentUser._id || currentUser.id || currentUser.email;
      const sessionKey = `comsappProvisioned:${userId}`;
      if (sessionStorage.getItem(sessionKey)) return;

      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        await axios.post(
          `${API_URL}/api/connector/provision-me`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        sessionStorage.setItem(sessionKey, '1');
      } catch {
        // non-blocking best-effort provisioning
      }
    };

    autoProvision();
  }, [currentUser]);

  const loginUser = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/api/users/login`, userData);
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('encryptionKey', response.data.encryptionKey);
      
      // Store the entire user object with permission flags
      const user = {
        ...response.data.user,
        education: response.data.user.education || false,
        health: response.data.user.health || false,
        harvesting: response.data.user.harvesting || false,
        communityOutreach: response.data.user.communityOutreach || false,
      };
      
      setCurrentUser(user);
      updateLinkingStatus(user);
      setEncryptionKey(response.data.encryptionKey);
      
      return { success: true };
    } catch (error) {
      console.error('Login failed', error);
      const status = error.response?.status;
      const retryAfterHeader = error.response?.headers?.['retry-after'];
      const rateLimitReset = error.response?.headers?.['ratelimit-reset'];
      const retryAfterSeconds = Number(retryAfterHeader || rateLimitReset || 0);
      return { 
        success: false, 
        status,
        retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : 0,
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const { firstName, lastName, email, password, cardNumber, userType } = userData;
      await axios.post(`${API_URL}/api/users/register`, {
        firstName,
        lastName,
        email,
        password,
        cardNumber,
        userType: userType || 'user'
      });
      return true;
    } catch (error) {
      console.error('Registration error', error);
      setError(error.response?.data?.message || 'Registration failed');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('encryptionKey');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    setComsappLinking(null);
    setEncryptionKey(null);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/me`);
      const user = {
        ...response.data,
        education: response.data.education || false,
        health: response.data.health || false,
        harvesting: response.data.harvesting || false,
        communityOutreach: response.data.communityOutreach || false,
      };
      setCurrentUser(user);
      updateLinkingStatus(user);
    } catch (error) {
      console.error('Error refreshing user', error);
    }
  };

  const getUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      return response.data;
    } catch (error) {
      console.error('Error fetching users', error);
      throw error;
    }
  };

  const getEngagementSummary = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/api/users/engagement-summary`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching engagement summary', error);
      throw error;
    }
  };

  const trackSectionAccess = async (section, path = '') => {
    try {
      const token = localStorage.getItem('authToken');
      const payload = {
        eventType: 'section_access',
        section: section || 'dashboard',
        path: path || ''
      };

      await axios.post(`${API_URL}/api/users/engagement/track`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      // Non-blocking for analytics; do not disrupt the user experience.
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`);
    } catch (error) {
      console.error('Error deleting user', error);
      throw error;
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      // Add email hash calculation when email is being updated
      const updatedData = { ...userData };
      
      // If updating email, prepare data for updating emailHash on server
      if (updatedData.email) {
        updatedData.updateEmailHash = true;
      }
      
      // Convert boolean fields explicitly if they're present
      if (updatedData.education !== undefined) {
        updatedData.education = Boolean(updatedData.education);
      }
      if (updatedData.health !== undefined) {
        updatedData.health = Boolean(updatedData.health);
      }
      if (updatedData.harvesting !== undefined) {
        updatedData.harvesting = Boolean(updatedData.harvesting);
      }
      if (updatedData.communityOutreach !== undefined) {
        updatedData.communityOutreach = Boolean(updatedData.communityOutreach);
      }
      
      const response = await axios.put(`${API_URL}/api/users/${userId}`, updatedData);
      return response.data;
    } catch (error) {
      console.error('Error updating user', error);
      if (error.response) {
        // The request was made and the server responded with a status code outside 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      throw error;
    }
  };

  // Manual re-link/reactivate ComSapp account (safe to call multiple times)
  const relinkComsappAccount = async () => {
    try {
      setRelinkingLoading(true);
      const response = await axios.post(`${API_URL}/api/connector/relink`, {});
      
      // Refresh user data to get updated linking status
      await refreshUser();
      
      return { 
        success: true, 
        message: response.data?.message || 'Account re-linked successfully',
        relinked: response.data?.relinked || []
      };
    } catch (error) {
      console.error('Error relinking ComSapp account', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to relink account'
      };
    } finally {
      setRelinkingLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      currentUser, 
      loading, 
      error, 
      loginUser, 
      register, 
      logout,
      refreshUser,
      getUsers,
      getEngagementSummary,
      trackSectionAccess,
      deleteUser,
      updateUser,
      relinkComsappAccount,
      relinkingLoading,
      comsappLinking,
      encryptionKey
    }}>
      {children}
    </AuthContext.Provider>
  );
};